// ABOUTME: Campaign list tab for admin campaign management
// ABOUTME: Displays list of campaigns with clickable navigation to detail pages
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Button,
  Box as MuiBox,
  Collapse,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import CreateCampaignForm from './CreateCampaignForm';
import { getCurrentAccessToken } from '../../services/api';

interface EmailCampaignStepDto {
  id: number;
  stepOrder: number;
  delayInHours: number;
  templateName: string;
  status: string;
  kbArticleId?: number | null;
  kbArticleTitle?: string | null;
}

interface EmailCampaignListDto {
  id: number;
  name: string;
  campaignType: string;
  description: string;
  stepCount: number;
  activeEnrollments: number;
  totalEmailsSent: number;
  isActive: boolean;
  steps: EmailCampaignStepDto[];
}

interface PagedResponse {
  data: EmailCampaignListDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

function CampaignRow({
  campaign,
}: {
  campaign: EmailCampaignListDto;
}) {
  const navigate = useNavigate();

  return (
    <TableRow hover>
      <TableCell
        component="th"
        scope="row"
        sx={{
          cursor: 'pointer',
          color: 'primary.main',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
        onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
      >
        {campaign.name}
      </TableCell>
      <TableCell>
        <Chip
          label={campaign.isActive ? 'Active' : 'Inactive'}
          color={campaign.isActive ? 'success' : 'error'}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Chip label={campaign.campaignType} size="small" />
      </TableCell>
      <TableCell>{campaign.description}</TableCell>
      <TableCell align="center">{campaign.stepCount}</TableCell>
      <TableCell align="center">{campaign.activeEnrollments}</TableCell>
    </TableRow>
  );
}

export default function CampaignListTab() {
  const [campaigns, setCampaigns] = useState<EmailCampaignListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCampaigns is a plain function in component scope; the effect correctly re-runs when the listed deps change, not when the function reference changes
  }, [page, pageSize]);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<PagedResponse>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns`,
        {
          params: {
            page: page + 1, // Backend uses 1-based pagination
            pageSize: pageSize,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCampaigns(response.data.data);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
      console.error('Error fetching campaigns:', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFormSuccess = () => {
    setShowCreateForm(false);
    fetchCampaigns(); // Refresh the campaigns list
  };

  const handleFormCancel = () => {
    setShowCreateForm(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <MuiBox sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create Campaign'}
        </Button>
      </MuiBox>

      <Collapse in={showCreateForm}>
        <MuiBox sx={{ mb: 3 }}>
          <CreateCampaignForm
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </MuiBox>
      </Collapse>

      <TableContainer component={Paper}>
        <Table aria-label="email campaigns table">
          <TableHead>
            <TableRow>
              <TableCell>Campaign Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Steps</TableCell>
              <TableCell align="center">Active Enrollments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
